import {
  BulkOwnershipInput,
  BulkOwnershipReview,
  BulkOwnershipResult,
} from '../types/payout-ownership.types';
import { instance } from './instance';
import {
  ConfigurePayoutAgreementInput,
  RecordPayoutServiceDayInput,
  PayoutServiceDay,
  PayoutAgreementSummary,
  PayoutCohortSummary,
  PayoutRiderOwnership,
  PayoutPreview,
  PayoutPreviewInput,
} from '../types/payout-agreements.types';

export const payoutAgreementsApi = {
  previewOwnership: async (
    input: BulkOwnershipInput
  ): Promise<BulkOwnershipReview> => {
    const review = (
      await instance.post('/payout/rider-ownership/bulk/preview', input)
    ).data.data;
    const ids = new Set(input.riderIds);
    if (
      !review ||
      !/^[a-f0-9]{64}$/.test(review.reviewHash) ||
      typeof review.ownershipLabel !== 'string' ||
      typeof review.reason !== 'string' ||
      !Number.isFinite(Date.parse(review.effectiveFrom)) ||
      (review.effectiveTo !== null &&
        !Number.isFinite(Date.parse(review.effectiveTo))) ||
      !Array.isArray(review.rows) ||
      review.rows.length !== ids.size ||
      new Set(review.rows.map((row: any) => row.riderId)).size !== ids.size ||
      review.rows.some(
        (row: any) =>
          !ids.has(row.riderId) ||
          !['READY', 'COVERED', 'CONFLICT'].includes(row.status) ||
          !Array.isArray(row.proposedPeriods) ||
          !Array.isArray(row.savedPeriods) ||
          typeof row.riderName !== 'string' ||
          typeof row.riderStatus !== 'string' ||
          (row.status === 'READY') !== Boolean(row.proposedPeriods.length) ||
          [...row.savedPeriods, ...row.proposedPeriods].some(
            (period: any) =>
              !Number.isFinite(Date.parse(period.effectiveFrom)) ||
              (period.effectiveTo !== null &&
                (!Number.isFinite(Date.parse(period.effectiveTo)) ||
                  period.effectiveTo <= period.effectiveFrom))
          )
      ) ||
      review.counts?.selected !== ids.size ||
      review.counts.ready !==
        review.rows.filter((row: any) => row.status === 'READY').length ||
      review.counts.covered !==
        review.rows.filter((row: any) => row.status === 'COVERED').length ||
      review.counts.conflicts !==
        review.rows.filter((row: any) => row.status === 'CONFLICT').length ||
      review.counts.periods !==
        review.rows.reduce(
          (sum: number, row: any) => sum + row.proposedPeriods.length,
          0
        )
    )
      throw new Error(
        'The ownership review is unavailable or incomplete. Reload it before saving.'
      );
    return review;
  },
  applyOwnership: async (
    input: BulkOwnershipInput & { expectedReviewHash: string }
  ): Promise<BulkOwnershipResult> => {
    const result = (
      await instance.post('/payout/rider-ownership/bulk/apply', input)
    ).data.data;
    const ids = new Set(input.riderIds);
    if (
      !result ||
      !Number.isSafeInteger(result.createdPeriods) ||
      result.createdPeriods < 0 ||
      ['createdRiderIds', 'coveredRiderIds', 'conflictRiderIds'].some(
        (key) =>
          !Array.isArray(result[key]) ||
          result[key].some((id: number) => !ids.has(id))
      )
    )
      throw new Error(
        'The save response is incomplete. Refresh ownership history before trying again.'
      );
    const classified = [
      ...result.createdRiderIds,
      ...result.coveredRiderIds,
      ...result.conflictRiderIds,
    ];
    if (
      classified.length !== ids.size ||
      new Set(classified).size !== ids.size ||
      result.createdPeriods < result.createdRiderIds.length ||
      (!result.createdRiderIds.length && result.createdPeriods !== 0)
    )
      throw new Error(
        'The save response is incomplete. Refresh ownership history before trying again.'
      );
    return result;
  },
  listOwnership: async (riderId: number): Promise<PayoutRiderOwnership[]> => {
    const records = (await instance.get(`/payout/riders/${riderId}/ownership`))
      .data.data;
    if (
      !Array.isArray(records) ||
      records.some((record) => record.riderId !== riderId)
    )
      throw new Error('Payout ownership history is unavailable.');
    return records;
  },
  assignShared: async (payload: {
    revisionId: string;
    assignments: ConfigurePayoutAgreementInput['assignment'][];
  }) =>
    (await instance.post('/payout/agreement-assignments/bulk', payload)).data
      .data,
  assignRidersToCohort: async (
    cohortId: string,
    payload: { riderIds: number[]; effectiveFrom: string; effectiveTo?: string }
  ): Promise<{ addedRiderIds: number[]; skippedRiderIds: number[] }> =>
    (
      await instance.post(
        `/payout/cohorts/${encodeURIComponent(cohortId)}/memberships/bulk`,
        payload
      )
    ).data.data,

  listServiceDays: async (riderId: number): Promise<PayoutServiceDay[]> =>
    (
      await instance.get('/payout/agreements/service-days', {
        params: { riderId },
      })
    ).data.data,
  recordServiceDay: async (
    payload: RecordPayoutServiceDayInput
  ): Promise<PayoutServiceDay> =>
    (await instance.post('/payout/agreements/service-days', payload)).data.data,
  partnerAccounts: async (
    partnerId: number
  ): Promise<
    Array<{
      id: number;
      accountName: string;
      accountNumber: string;
      bankName: string;
      isActive: boolean;
    }>
  > => (await instance.get(`/payout/partners/${partnerId}/accounts`)).data.data,
  selectPartnerAccount: async (partnerId: number, bankDetailId: number) =>
    (
      await instance.post(`/payout/partners/${partnerId}/accounts/select`, {
        bankDetailId,
      })
    ).data.data,
  listAgreements: async (): Promise<PayoutAgreementSummary[]> =>
    (await instance.get('/payout/agreements')).data.data,

  listCohorts: async (): Promise<PayoutCohortSummary[]> =>
    (await instance.get('/payout/cohorts')).data.data,

  configure: async (payload: ConfigurePayoutAgreementInput) =>
    (await instance.post('/payout/agreements/configure', payload)).data.data,

  preview: async (payload: PayoutPreviewInput): Promise<PayoutPreview> =>
    (await instance.post('/payout/preview-standard-delivery', payload)).data
      .data,

  createCohort: async (payload: {
    name: string;
  }): Promise<{ id: string; name: string }> =>
    (await instance.post('/payout/cohorts', payload)).data.data,

  assignRiderToCohort: async (
    cohortId: string,
    payload: { riderId: number; effectiveFrom: string; effectiveTo?: string }
  ) =>
    (await instance.post(`/payout/cohorts/${cohortId}/memberships`, payload))
      .data.data,

  endCohortMembership: async (
    cohortId: string,
    membershipId: string,
    payload: { effectiveTo: string; reason: string }
  ) =>
    (
      await instance.post(
        `/payout/cohorts/${encodeURIComponent(
          cohortId
        )}/memberships/${encodeURIComponent(membershipId)}/end`,
        payload
      )
    ).data.data,

  assignRiderAttribution: async (payload: {
    riderId: number;
    ownershipType: 'INDEPENDENT' | 'PARTNER';
    partnerId?: number;
    effectiveFrom: string;
    effectiveTo?: string;
    supersedesAttributionId?: string;
  }) => (await instance.post('/payout/rider-attributions', payload)).data.data,
};
